/**
 * TanStack Query hooks for the comments API.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchComments,
  createComment,
  updateComment,
  deleteComment,
  toggleReaction,
  fetchCommentCounts,
  type CommentCreatePayload,
  type CommentsResponse,
} from "../api/comments";

export function useComments(
  projectId: string,
  targetType: string,
  targetId: string | null,
  branch: string,
  options?: { includeAllBranches?: boolean },
) {
  return useQuery<CommentsResponse>({
    queryKey: ["comments", projectId, targetType, targetId, branch, options?.includeAllBranches],
    queryFn: () =>
      fetchComments(projectId, {
        target_type: targetType,
        target_id: targetId!,
        branch,
        include_all_branches: options?.includeAllBranches,
      }),
    enabled: !!projectId && !!targetId,
  });
}

export function useCreateComment(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CommentCreatePayload) => createComment(projectId, payload),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["comments", projectId, variables.target_type, variables.target_id],
      });
      qc.invalidateQueries({ queryKey: ["comment-counts", projectId] });
    },
  });
}

export function useUpdateComment(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { commentId: string; body?: string; resolved?: boolean }) =>
      updateComment(projectId, args.commentId, {
        body: args.body,
        resolved: args.resolved,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["comments", projectId] });
      qc.invalidateQueries({ queryKey: ["comment-counts", projectId] });
    },
  });
}

export function useDeleteComment(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => deleteComment(projectId, commentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["comments", projectId] });
      qc.invalidateQueries({ queryKey: ["comment-counts", projectId] });
    },
  });
}

export function useToggleReaction(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { commentId: string; emoji: string }) =>
      toggleReaction(projectId, args.commentId, args.emoji),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["comments", projectId] });
    },
  });
}

export function useCommentCounts(
  projectId: string,
  branch: string,
  nodeIds?: string[],
) {
  return useQuery<Record<string, number>>({
    queryKey: ["comment-counts", projectId, branch, nodeIds],
    queryFn: () => fetchCommentCounts(projectId, branch, nodeIds),
    enabled: !!projectId,
    staleTime: 30_000,
  });
}
